/**
 * Compliance portal: CRMLayout from @evo-flywheel/ui with a grouped, tone-coded left rail,
 * descriptor-generated modules (generic List/Show/Create/Edit over the CQRS provider), and
 * the kit's own surfaces — cruscotto, guided journeys (pack-journeys), rule tables (pack-decisions).
 */
import React from "react";
import { Authenticated, Refine } from "@refinedev/core";
import { ErrorComponent, useNotificationProvider } from "@refinedev/antd";
import routerProvider, { DocumentTitleHandler, UnsavedChangesNotifier } from "@refinedev/react-router-v6";
import { BrowserRouter, Navigate, Outlet, Route, Routes } from "react-router-dom";
import { App as AntdApp, ConfigProvider } from "antd";
import itIT from "antd/locale/it_IT";
import { CRMLayout, authProvider, customTheme, descriptorTextTransformers, initFlywheelRegistry, italianI18nProvider, registerIcons, setPortalChrome } from "@evo-flywheel/ui";
import { descriptors } from "@kit/descriptors/index";
import { coreBOSMap } from "@kit/corebos-map";
import { dataProvider } from "./dataProviders";
import { kitResources, refineResources } from "./kit-resources";
import { instance } from "./instance";
import { buildNav } from "./nav";
import { MODULE_ICONS } from "./icons";
import { LoginPage } from "./pages/LoginPage";
import { GenericCreate, GenericEdit } from "./pages/GenericForm";
import { GenericList } from "./pages/GenericList";
import { GenericShow } from "./pages/GenericShow";
import { Dashboard } from "./pages/Dashboard";
import { JourneyList, JourneyRunner } from "./pages/Wizard";
import { Rules } from "./pages/Rules";
// boot: module slots (kit + instance layers) and the shared related column
import "./modules";
import "./modules/related";
import "./modules/org";

registerIcons(MODULE_ICONS);
initFlywheelRegistry({ descriptors, corebosMap: coreBOSMap });
setPortalChrome({
  brand: { logoSrc: instance.logoSrc ?? "/logo.png", logoAlt: instance.brand },
  assistant: {
    title: "Assistente compliance",
    emptyState: "Chiedi del registro dei trattamenti, dei fornitori, dei rischi o delle verifiche aperte.",
    placeholder: "Es. quali fornitori accedono a dati sanitari?",
    quickActions: [
      { key: "find", icon: "SearchOutlined", label: "Cerca…", prompt: "/find " },
      { key: "gaps", icon: "SafetyCertificateOutlined", label: "Cosa manca?", prompt: "Quali obblighi bloccanti sono aperti?" },
    ],
  },
});

const enabled = instance.modules?.length ? kitResources.filter((resource) => instance.modules!.includes(resource.name)) : kitResources;
const enabledNames = new Set(enabled.map((resource) => resource.name));
const resources = refineResources.filter((resource) => enabledNames.has(resource.name));
const nav = buildNav(enabled);
// Titles use the descriptors' Italian labels, never Refine's English inflector (UI contract rule 11).
const textTransformers = descriptorTextTransformers(descriptors);
const i18nProvider = italianI18nProvider({}, { resourceLabel: (slug) => descriptors.find((d) => d.slug === slug)?.label.singular });

const App: React.FC = () => <BrowserRouter><ConfigProvider theme={customTheme} locale={itIT}><AntdApp><Refine routerProvider={routerProvider} dataProvider={dataProvider} authProvider={authProvider} notificationProvider={useNotificationProvider} i18nProvider={i18nProvider} resources={resources} options={{ syncWithLocation: true, warnWhenUnsavedChanges: true, disableTelemetry: true, textTransformers }}>
  <Routes>
    <Route path="/login" element={<LoginPage />} />
    <Route element={<Authenticated key="protected" fallback={<LoginPage />}><CRMLayout nav={nav} navMode={instance.navigation ?? "groups"}><Outlet /></CRMLayout></Authenticated>}>
      <Route index element={<Navigate to="/dashboard" replace />} />
      <Route path="dashboard" element={<Dashboard />} />
      <Route path="wizard" element={<JourneyList />} />
      <Route path="wizard/:id" element={<JourneyRunner />} />
      <Route path="regole" element={<Rules />} />
      {enabled.map((resource) => <Route key={resource.name} path={resource.name}><Route index element={<GenericList />} /><Route path="show/:id" element={<GenericShow />} /><Route path="create" element={<GenericCreate />} /><Route path="edit/:id" element={<GenericEdit />} /></Route>)}
      <Route path="*" element={<ErrorComponent />} />
    </Route>
  </Routes>
  <UnsavedChangesNotifier /><DocumentTitleHandler handler={({ resource }) => `${(resource?.meta as { label?: string } | undefined)?.label ?? "Compliance"} · ${instance.brand}`} />
</Refine></AntdApp></ConfigProvider></BrowserRouter>;

export default App;
